import { connectRoom } from './room-connection.js';
import { canShowIOSAppLink, nativeInvitationURL } from './invitation-links.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createClient } from '@supabase/supabase-js';
import { ProcessingModal } from './processing-modal.jsx';

const waitingEvents = ['room.player_joined', 'match.started', 'rematch.created'];
let pagesSupabaseClient = null;

function readState(id) {
    const element = document.getElementById(id);
    if (!element?.textContent) {
        return null;
    }

    try {
        return JSON.parse(element.textContent);
    } catch {
        return null;
    }
}

function getSupabaseClient() {
    const url = import.meta.env.VITE_SUPABASE_URL
        || document.querySelector('meta[name="supabase-url"]')?.content;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
        || document.querySelector('meta[name="supabase-anon-key"]')?.content;

    if (!url || !anonKey) {
        return null;
    }

    if (pagesSupabaseClient === null) {
        pagesSupabaseClient = createClient(url, anonKey);
    }

    return pagesSupabaseClient;
}

function diagnosticsEnabled() {
    return document.querySelector('meta[name="secret-digits-realtime-diagnostics"]')?.content === 'enabled';
}

function emitRealtimeDiagnostic(detail) {
    if (!diagnosticsEnabled()) {
        return;
    }

    const safeDetail = {
        scope: 'waiting',
        occurred_at: new Date().toISOString(),
        ...detail,
    };
    window.dispatchEvent(new CustomEvent('secret-digits:realtime-diagnostic', { detail: safeDetail }));
    console.info('[SecretDigitsRealtime]', safeDetail);
}

async function postJson(path, body, csrfToken) {
    const response = await fetch(path, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken || '',
        },
        body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(payload.message || '操作に失敗しました。');
    }

    return payload;
}

function CopyButton({ text, label }) {
    const [copied, setCopied] = useState(false);

    return (
        <button
            type="button"
            className="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md border border-transparent text-zinc-300 hover:border-red-400 hover:bg-zinc-800 hover:text-red-200"
            aria-label={label}
            onClick={async () => {
                await navigator.clipboard?.writeText(text);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1400);
            }}
            title={copied ? 'コピーしました' : label}
        >
            {copied ? (
                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                </svg>
            ) : (
                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
            )}
        </button>
    );
}

function BrandMark() {
    return (
        <span className="sd-brand-mark" aria-hidden="true">
            <svg className="h-7 w-7" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="2.2" />
                <path d="M16 6v3M26 16h-3M16 26v-3M6 16h3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <circle cx="16" cy="16" r="3" fill="currentColor" />
                <path d="m18.2 13.9 4.1-3.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <circle cx="23" cy="10.2" r="1.5" fill="currentColor" />
            </svg>
        </span>
    );
}

function UiIcon({ name }) {
    const paths = {
        play: <path d="m9 7 8 5-8 5V7Z" />,
        plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
        enter: <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="m10 17 5-5-5-5" /><path d="M15 12H3" /></>,
        arrow: <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>,
        back: <><path d="M19 12H5" /><path d="m11 18-6-6 6-6" /></>,
    };

    return (
        <svg aria-hidden="true" className="sd-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {paths[name]}
        </svg>
    );
}

function BrandHeading({ title, subtitle, showBrandLabel = true }) {
    return (
        <div className="relative z-10 flex items-center gap-4">
            <BrandMark />
            <div>
                {showBrandLabel && <p className="text-xs font-bold uppercase tracking-[0.16em] text-red-400">Secret Digits</p>}
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{title}</h1>
                {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
            </div>
        </div>
    );
}

function LobbyApp({ initial }) {
    const invitationMode = initial.invitation_mode === true;
    const invitedRoom = initial.invited_room;
    const [showIOSAppLink, setShowIOSAppLink] = useState(false);
    useEffect(() => { setShowIOSAppLink(canShowIOSAppLink()); }, []);
    const nativeURL = showIOSAppLink && invitationMode && invitedRoom && typeof window !== 'undefined'
        ? nativeInvitationURL(invitedRoom.room_code, window.location.origin) : null;
    const [created, setCreated] = useState(null);
    const [roomCode, setRoomCode] = useState(initial.prefill_room_code || '');
    const [playerName, setPlayerName] = useState('');
    const [digitLength, setDigitLength] = useState(3);
    const [ruleset, setRuleset] = useState('classic');
    const [error, setError] = useState('');
    const [processing, setProcessing] = useState('');
    const [sessionCheck, setSessionCheck] = useState(initial.backend === 'cloudflare' ? 'checking' : 'ready');
    const [resumable, setResumable] = useState(null);
    const refreshSession = useCallback(async () => {
        if (initial.backend !== 'cloudflare') return;
        setSessionCheck('checking');
        try {
            const response = await fetch('/api/web/session', { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(10000) });
            if (!response.ok) throw new Error('Session check failed');
            const summary = await response.json();
            setResumable(summary);
            setSessionCheck('ready');
        } catch { setSessionCheck('error'); }
    }, [initial.backend]);
    useEffect(() => {
        void refreshSession();
        const shown = (event) => {
            if (event.persisted) { window.location.replace('/'); return; }
            void refreshSession();
        };
        window.addEventListener('pageshow', shown);
        window.addEventListener('focus', refreshSession);
        return () => { window.removeEventListener('pageshow', shown); window.removeEventListener('focus', refreshSession); };
    }, [refreshSession]);
    const lobbyBlocked = processing !== '' || sessionCheck !== 'ready' || resumable?.available;
    const resumeSession = async () => {
        setProcessing('resume'); setError('');
        try {
            const response = await fetch('/api/web/session/resume', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Secret-Digits-Web': '1' }, body: '{}', signal: AbortSignal.timeout(10000) });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.message || '復帰できませんでした。');
            window.location.assign(resumable.resume_url);
        } catch (failure) { setError(failure.message); await refreshSession(); }
        finally { setProcessing(''); }
    };
    const exitSavedSession = async () => {
        if (!window.confirm('この対戦から退出すると、同じプレイヤーでは復帰できなくなります。退出しますか？')) return;
        setProcessing('exit'); setError('');
        try {
            const response = await fetch('/api/web/session/clear', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Secret-Digits-Web': '1' }, body: '{}', signal: AbortSignal.timeout(10000) });
            if (!response.ok) throw new Error('退出できませんでした。通信状況を確認して再試行してください。');
            window.location.replace('/');
        } catch (failure) { setError(failure.message); setProcessing(''); }
    };

    const createRoom = async (event) => {
        event.preventDefault();
        if (lobbyBlocked) return;
        setError('');
        setProcessing('create');
        try {
            const response = await fetch('/api/v1/rooms', {
                method: 'POST',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ digit_length: digitLength, ruleset }),
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.message || 'ルーム作成に失敗しました。');
            }
            setCreated(payload);
            setRoomCode(payload.room_code);
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    const joinRoom = async (event) => {
        event.preventDefault();
        if (lobbyBlocked) return;
        setError('');
        setProcessing('join');
        try {
            const normalizedRoomCode = roomCode.trim().toUpperCase();
            if (initial.backend === 'cloudflare') {
                const response = await fetch(`/api/v1/rooms/${encodeURIComponent(normalizedRoomCode)}/join`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-Secret-Digits-Web': '1',
                    },
                    body: JSON.stringify({ player_name: playerName.trim() }),
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(payload.message || '参加に失敗しました。');
                }
                window.location.assign(`/rooms/${encodeURIComponent(normalizedRoomCode)}/waiting`);
            } else {
                const payload = await postJson(`/rooms/${encodeURIComponent(normalizedRoomCode)}/join-session`, {
                    player_name: playerName.trim(),
                }, initial.csrf_token);
                window.location.assign(payload.waiting_url);
            }
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    return (
        <main className="mx-auto flex min-h-screen w-full max-w-6xl flex-col gap-7 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
            <header className="sd-hero rounded-2xl border p-6 sm:p-8">
                <BrandHeading showBrandLabel={invitationMode} title={invitationMode ? '招待ルームへ参加' : 'Secret Digits'} subtitle={invitationMode ? '招待されたゲームへ参加します' : '数字を読み合う、シンプルな頭脳戦'} />
                {!invitationMode && <div className="relative z-10 mt-6">
                    <a href={initial.solo_url} className="sd-button-secondary inline-flex items-center justify-center gap-2 border border-zinc-700 px-5 py-2.5 text-sm font-semibold text-zinc-200 hover:border-red-400 hover:text-red-300">
                        <UiIcon name="play" />1人で遊ぶ
                    </a>
                </div>}
            </header>

            {initial.backend === 'cloudflare' && <aside className="rounded-xl border border-zinc-700 bg-zinc-900 p-4 text-sm text-zinc-200" aria-live="polite">
                {sessionCheck === 'checking' ? <p>保存済みの対戦を確認中…</p> : sessionCheck === 'error' ? <><p>対戦情報を確認できません。通信状況を確認してください。</p><button type="button" onClick={refreshSession} className="mt-2 underline">再試行</button></> : resumable?.available ? <>
                    <p className="font-semibold">進行中の対戦があります</p>
                    <div className="mt-3 flex flex-wrap gap-4"><button type="button" onClick={resumeSession} disabled={processing !== ''} className="rounded-lg bg-red-600 px-4 py-3 font-semibold text-white">対戦に戻る</button><button type="button" onClick={exitSavedSession} disabled={processing !== ''} className="underline">この対戦から退出</button></div>
                </> : resumable?.reason && <p>以前の対戦は終了したか、復帰期限が過ぎています。新しいルームで参加してください。</p>}
                <p className="mt-2 text-xs text-zinc-400">復帰は同じ端末・同じブラウザで、対戦の期限内に行ってください。Cookieを消すと復帰できません。秘密数字は端末に保存しません。</p>
            </aside>}

            {nativeURL && <aside className="mx-auto w-full max-w-2xl text-sm" aria-label="iOSアプリで参加">
                <a href={nativeURL} className="inline-flex min-h-11 items-center rounded-lg border px-4 py-2 font-semibold underline">iOSアプリで開く</a>
                <p className="mt-2 text-xs text-zinc-500">インストール済みの方はこちら。開けない場合は、このままWebで参加できます。</p>
            </aside>}

            <ProcessingModal show={processing !== ''} />

            {error && <p className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</p>}

            {invitationMode && !invitedRoom ? (
                <section className="sd-surface mx-auto w-full max-w-2xl rounded-2xl border border-red-500/40 bg-zinc-900 p-6">
                    <h2 className="text-lg font-semibold text-zinc-50">招待ルームが見つかりません</h2>
                    <p className="mt-3 text-sm text-zinc-400">URLが正しいか、ルームの有効期限が切れていないかを確認してください。</p>
                    <a href={initial.lobby_url} className="mt-5 inline-flex rounded-lg border border-zinc-600 px-4 py-2 text-sm font-semibold text-zinc-200 hover:border-zinc-400">ロビーへ戻る</a>
                </section>
            ) : <section className={`grid gap-6 ${invitationMode ? 'mx-auto w-full max-w-2xl' : 'lg:grid-cols-2'}`}>
                {!invitationMode && <div className="sd-surface flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900 p-6 sm:p-7">
                    <h2 className="text-lg font-semibold text-zinc-50">ルーム作成</h2>
                    <form className="mt-6 flex flex-1 flex-col gap-4" onSubmit={createRoom}>
                        <div>
                            <label className="mb-2 block text-sm text-zinc-300" htmlFor="digit_length">桁数</label>
                            <select
                                id="digit_length"
                                className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
                                value={digitLength}
                                onChange={(event) => {
                                    const nextDigitLength = Number(event.target.value);
                                    setDigitLength(nextDigitLength);
                                    if (nextDigitLength !== 4) {
                                        setRuleset('classic');
                                    }
                                }}
                                disabled={Boolean(lobbyBlocked)}
                            >
                                <option value="3">3桁（ターン2分）</option>
                                <option value="4">4桁（ターン5分）</option>
                            </select>
                        </div>
                        {digitLength === 4 && (
                            <div>
                                <label className="mb-2 block text-sm text-zinc-300" htmlFor="ruleset">ゲームモード</label>
                                <select
                                    id="ruleset"
                                    className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100"
                                    value={ruleset}
                                    onChange={(event) => setRuleset(event.target.value)}
                                    disabled={Boolean(lobbyBlocked)}
                                >
                                    <option value="classic">Classic（アイテムなし）</option>
                                    <option value="tactics">Tactics</option>
                                </select>
                            </div>
                        )}
                        {created && (
                            <div className="space-y-3 rounded-xl border border-red-500/30 bg-zinc-950 p-4">
                                <ReadOnlyCopy id="created_room_code" label="ルームコード" value={created.room_code} copyLabel="ルームコードをコピー" mono />
                                <ReadOnlyCopy id="created_invite_url" label="招待URL" value={created.invite_url} copyLabel="招待URLをコピー" />
                            </div>
                        )}
                        <div className="lg:mt-auto">
                            <button type="submit" className="sd-button-primary inline-flex w-full items-center justify-center gap-2 bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-500" disabled={Boolean(lobbyBlocked)}>
                            <UiIcon name="plus" />ルームを作成
                            </button>
                        </div>
                    </form>
                </div>}

                <div className="sd-surface flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900 p-6 sm:p-7">
                    <h2 className="text-lg font-semibold text-zinc-50">{invitationMode ? '対戦設定' : 'ルーム参加'}</h2>
                    {invitationMode && invitedRoom && (
                        <dl className="mt-5 grid grid-cols-2 gap-3 rounded-xl border border-red-500/30 bg-zinc-950 p-4 text-sm">
                            <div className="col-span-2"><dt className="text-zinc-500">ルームコード</dt><dd className="mt-1 font-mono font-semibold tracking-widest text-red-300">{invitedRoom.room_code}</dd></div>
                            <div><dt className="text-zinc-500">桁数</dt><dd className="mt-1 font-semibold text-zinc-100">{invitedRoom.digit_length}桁</dd></div>
                            <div><dt className="text-zinc-500">モード</dt><dd className="mt-1 font-semibold text-zinc-100">{invitedRoom.ruleset === 'tactics' ? 'Tactics' : 'Classic'}</dd></div>
                        </dl>
                    )}
                    <form className="mt-6 flex flex-1 flex-col gap-4" onSubmit={joinRoom}>
                        {!invitationMode && <div>
                            <label className="mb-2 block text-sm text-zinc-300" htmlFor="room_code">ルームコード</label>
                            <input id="room_code" value={roomCode} onChange={(event) => setRoomCode(event.target.value.toUpperCase())} maxLength="8" className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm uppercase tracking-widest text-zinc-100" placeholder="A1B2C3D4" />
                        </div>}
                        <div>
                            <label className="mb-2 block text-sm text-zinc-300" htmlFor="player_name">プレイヤー名</label>
                            <p className="mb-2 text-xs text-zinc-400">対戦相手に表示されます。実名や連絡先を入れる必要はありません。</p>
                            <input id="player_name" value={playerName} onChange={(event) => setPlayerName(event.target.value)} maxLength="32" className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" placeholder="Player1" />
                        </div>
                        <div className="lg:mt-auto">
                            <button type="submit" className="sd-button-primary inline-flex w-full items-center justify-center gap-2 bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-500" disabled={Boolean(lobbyBlocked)}>
                            <UiIcon name="enter" />参加する
                            </button>
                        </div>
                    </form>
                </div>
            </section>}
        </main>
    );
}

function ReadOnlyCopy({ id, label, value, copyLabel, mono = false }) {
    return (
        <div>
            <label className="mb-2 block text-sm text-zinc-300" htmlFor={id}>{label}</label>
            <div className="relative">
                <input id={id} type="text" readOnly value={value} className={`w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 pr-11 text-sm text-zinc-100 ${mono ? 'font-mono tracking-widest text-red-300' : ''}`} />
                <CopyButton text={value} label={copyLabel} />
            </div>
        </div>
    );
}

function WaitingApp({ initial }) {
    const [room, setRoom] = useState(null);
    const [status, setStatus] = useState('fallback');
    const [leaving, setLeaving] = useState(false);
    const [leaveError, setLeaveError] = useState('');
    const leave = async () => {
        setLeaving(true);
        try {
            const response = await fetch('/api/web/session/clear', { method: 'POST', headers: { 'X-Secret-Digits-Web': '1', 'Content-Type': 'application/json' }, body: '{}', signal: AbortSignal.timeout(5000) });
            if (!response.ok) throw new Error('退出できませんでした。もう一度お試しください。');
            window.location.assign(initial.lobby_url);
        } catch {
            setLeaveError('セッションを消去できませんでした。通信状況を確認して再試行してください。');
            setLeaving(false);
        }
    };
    const fetchRoom = useCallback(async () => {
        try {
            const response = await fetch(`/api/v1/rooms/${initial.room_code}`, {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                return;
            }
            const payload = await response.json();
            setRoom(payload);
            if ((payload.players || []).length >= 2 && payload.match_id) {
                window.location.assign(payload.battle_url || initial.battle_url);
            }
        } catch {
            setStatus('fallback');
        }
    }, [initial.battle_url, initial.room_code]);

    useEffect(() => {
        fetchRoom();
    }, [fetchRoom]);

    useEffect(() => {
        const resume = () => {
            if (document.visibilityState === 'visible') {
                fetchRoom();
            }
        };
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('pageshow', fetchRoom);
        return () => {
            document.removeEventListener('visibilitychange', resume);
            window.removeEventListener('pageshow', fetchRoom);
        };
    }, [fetchRoom]);

    useEffect(() => {
        if (initial.invalid_session) {
            return undefined;
        }
        const intervalMs = status === 'subscribed' ? 30000 : 8000;
        emitRealtimeDiagnostic({ event: 'poll.started', status, poll_mode: status === 'subscribed' ? 'steady' : 'fallback', poll_interval_ms: intervalMs });
        const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void fetchRoom(); }, intervalMs);
        return () => window.clearInterval(timer);
    }, [fetchRoom, initial.invalid_session, status]);

    useEffect(() => {
        if (initial.invalid_session) {
            return undefined;
        }
        if (initial.backend === 'cloudflare') {
            return connectRoom({ roomCode: initial.room_code, onState: fetchRoom, onStatus: setStatus });
        }
        const client = getSupabaseClient();
        const topic = room?.realtime_topic || initial.realtime_topic;
        if (!client || !topic) {
            setStatus('fallback');
            emitRealtimeDiagnostic({ event: 'subscription.skipped', status: 'fallback_only' });
            return undefined;
        }

        const channel = client.channel(topic);
        waitingEvents.forEach((eventName) => {
            channel.on('broadcast', { event: eventName }, () => {
                emitRealtimeDiagnostic({ event: 'broadcast.received', status: 'subscribed', broadcast_event: eventName });
                fetchRoom();
            });
        });
        channel.subscribe((nextStatus) => {
            setStatus(nextStatus === 'SUBSCRIBED' ? 'subscribed' : 'fallback');
            emitRealtimeDiagnostic({ event: 'subscription.status', status: nextStatus });
        });

        return () => {
            client.removeChannel(channel);
            emitRealtimeDiagnostic({ event: 'subscription.cleaned_up', status: 'cleaned_up' });
        };
    }, [fetchRoom, initial.backend, initial.invalid_session, initial.realtime_topic, initial.room_code, room?.realtime_topic]);

    if (initial.invalid_session) {
        return (
            <main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col gap-8 px-4 py-10 sm:px-6 lg:px-8">
                <Header title="ルーム待機" roomCode={initial.room_code} />
                <section className="sd-surface rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
                    <p className="text-sm text-rose-700">このブラウザには参加情報がありません。ロビーから参加し直してください。</p>
                    <a href={initial.join_url} className="mt-4 inline-flex rounded-lg border border-zinc-600 px-4 py-2 text-sm text-zinc-200">このルームへ参加し直す</a>
                </section>
            </main>
        );
    }

    const players = room?.players || [];
    return (
        <main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col gap-8 px-4 py-10 sm:px-6 lg:px-8">
            <Header title="ルーム待機" />
            <section className="sd-surface rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
                <div className="mb-6 space-y-3 rounded-xl border border-red-500/30 bg-zinc-950 p-4">
                    <ReadOnlyCopy id="waiting_room_code" label="ルームコード" value={initial.room_code} copyLabel="ルームコードをコピー" mono />
                    <ReadOnlyCopy id="waiting_invite_url" label="招待URL" value={initial.invite_url} copyLabel="招待URLをコピー" />
                </div>
                <h2 className="text-lg font-semibold text-zinc-100">参加プレイヤー</h2>
                <p className="mt-2 text-sm text-zinc-400">{room?.digit_length || 3}桁 ・ {room?.ruleset === 'tactics' ? 'Tactics' : 'Classic'}</p>
                <ul className="mt-4 space-y-2">
                    {players.map((player) => (
                        <li key={player.id} className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-200">
                            {player.name}{player.is_me ? '（あなた）' : ''}
                        </li>
                    ))}
                </ul>
                {players.length < 2 && <p className="mt-4 text-sm text-zinc-400">もう1人の参加を待っています...</p>}
                {initial.backend === 'cloudflare' && <button type="button" onClick={leave} disabled={leaving} className="mt-5 rounded-lg border border-zinc-600 px-4 py-2 text-sm text-zinc-200">退出してセッションを消去</button>}
                {leaveError && <p role="alert" className="mt-3 text-sm text-red-300">{leaveError}</p>}
            </section>
        </main>
    );
}

function Header({ title, roomCode }) {
    return (
        <header className="sd-hero rounded-2xl border p-6 sm:p-8">
            <BrandHeading title={title} />
            {roomCode && <p className="relative z-10 mt-4 text-sm text-zinc-300">ルームコード: <span className="font-mono font-semibold text-red-300">{roomCode}</span></p>}
        </header>
    );
}

function uniqueDigits(value, digitLength = 3) {
    const normalized = String(value || '').replace(/\D/g, '').slice(0, digitLength);
    return normalized.length === digitLength && new Set(normalized.split('')).size === normalized.length;
}

function SoloApp({ initial }) {
    const [started, setStarted] = useState(false);
    const [secret, setSecret] = useState('');
    const [botSecret, setBotSecret] = useState('');
    const [guess, setGuess] = useState('');
    const [turns, setTurns] = useState([]);
    const [result, setResult] = useState('');
    const [error, setError] = useState('');
    const digits = useMemo(() => ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'], []);

    const randomDigits = useCallback(() => {
        const shuffled = digits.slice().sort(() => Math.random() - 0.5);
        return shuffled.slice(0, 3).join('');
    }, [digits]);

    const evaluate = (answer, value) => {
        let hit = 0;
        let blow = 0;
        value.split('').forEach((digit, index) => {
            if (answer[index] === digit) {
                hit++;
            } else if (answer.includes(digit)) {
                blow++;
            }
        });
        return { hit, blow };
    };

    const start = (event) => {
        event.preventDefault();
        if (!uniqueDigits(secret)) {
            setError('秘密数字は重複なしの3桁で入力してください。');
            return;
        }
        setBotSecret(randomDigits());
        setStarted(true);
        setTurns([]);
        setResult('');
        setError('');
        setGuess('');
    };

    const submit = (event) => {
        event.preventDefault();
        if (!uniqueDigits(guess)) {
            setError('推測は重複なしの3桁で入力してください。');
            return;
        }
        const botGuess = randomDigits();
        const playerResult = evaluate(botSecret, guess);
        const botResult = evaluate(secret, botGuess);
        const nextTurns = [...turns, { turn: turns.length + 1, player_guess: guess, player: playerResult, bot_guess: botGuess, bot: botResult }];
        setTurns(nextTurns);
        setGuess('');
        setError('');
        if (playerResult.hit === 3) {
            setResult('あなたの勝利です。');
        } else if (botResult.hit === 3) {
            setResult('BOTの勝利です。');
        }
    };

    return (
        <main className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-7 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
            <header className="sd-hero rounded-2xl border p-6 sm:p-8">
                <BrandHeading title="1人モード" subtitle="BOTを相手に推理を練習できます" />
                <a href={initial.lobby_url} className="sd-button-secondary relative z-10 mt-6 inline-flex items-center justify-center gap-2 border border-zinc-600 px-5 py-2.5 text-sm font-semibold text-zinc-200 hover:border-red-400 hover:text-red-300">
                    <UiIcon name="back" />ロビーに戻る
                </a>
            </header>
            <section className="sd-surface rounded-2xl border border-zinc-800 bg-zinc-900 p-6 sm:p-7">
                {error && <p className="mb-4 text-sm font-semibold text-rose-700">{error}</p>}
                {!started ? (
                    <form className="grid gap-4" onSubmit={start}>
                        <input value={secret} onChange={(event) => setSecret(event.target.value.replace(/\D/g, '').slice(0, 3))} maxLength="3" className="rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm tracking-widest text-zinc-100" placeholder="秘密数字（重複なし3桁）" aria-label="秘密数字" />
                            <button type="submit" className="sd-button-primary inline-flex items-center justify-center gap-2 bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-500"><UiIcon name="play" />開始</button>
                    </form>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h2 className="text-lg font-semibold text-zinc-50">対戦中</h2>
                            <p className="text-sm text-zinc-400">ターン {turns.length}</p>
                        </div>
                        {!result && (
                            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={submit}>
                                <input value={guess} onChange={(event) => setGuess(event.target.value.replace(/\D/g, '').slice(0, 3))} maxLength="3" className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm tracking-widest text-zinc-100" placeholder="あなたの推測（3桁）" />
                                <button type="submit" className="inline-flex items-center justify-center rounded-lg bg-zinc-100 px-5 py-2.5 text-sm font-semibold text-zinc-900 hover:bg-white">推測する</button>
                            </form>
                        )}
                        {result && <p className="mt-4 rounded-lg border border-red-500/40 bg-zinc-950 px-4 py-3 text-sm font-semibold text-red-300">{result}</p>}
                        <SoloHistory turns={turns} />
                        <button type="button" onClick={() => setStarted(false)} className="mt-5 inline-flex items-center justify-center rounded-lg border border-zinc-600 px-4 py-2 text-sm font-semibold text-zinc-200 hover:border-zinc-400 hover:text-zinc-100">リセット</button>
                    </>
                )}
            </section>
        </main>
    );
}

function SoloHistory({ turns }) {
    return (
        <div className="mt-6 overflow-hidden rounded-xl border border-zinc-800">
            <table className="w-full text-left text-sm">
                <thead className="bg-zinc-950 text-zinc-300"><tr><th className="px-3 py-2">ターン</th><th className="px-3 py-2">あなた</th><th className="px-3 py-2">結果</th><th className="px-3 py-2">BOT</th><th className="px-3 py-2">結果</th></tr></thead>
                <tbody className="divide-y divide-zinc-800 bg-zinc-900">
                    {turns.length === 0 ? (
                        <tr><td colSpan="5" className="px-3 py-4 text-center text-zinc-500">まだターン履歴はありません。</td></tr>
                    ) : turns.map((turn) => (
                        <tr key={turn.turn}><td className="px-3 py-2 text-zinc-400">{turn.turn}</td><td className="px-3 py-2 font-mono text-zinc-200">{turn.player_guess}</td><td className="px-3 py-2 text-zinc-300">H{turn.player.hit} / B{turn.player.blow}</td><td className="px-3 py-2 font-mono text-zinc-200">{turn.bot_guess}</td><td className="px-3 py-2 text-zinc-300">H{turn.bot.hit} / B{turn.bot.blow}</td></tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function mount(rootId, stateId, Component) {
    const root = document.getElementById(rootId);
    const initial = readState(stateId);
    if (!root || !initial || root.dataset.reactMounted === 'true') {
        return;
    }
    root.dataset.reactMounted = 'true';
    createRoot(root).render(<Component initial={initial} />);
}

export function mountPageApps() {
    mount('secret-digits-lobby-root', 'secret-digits-lobby-state', LobbyApp);
    mount('secret-digits-waiting-root', 'secret-digits-waiting-state', WaitingApp);
    mount('secret-digits-solo-root', 'secret-digits-solo-state', SoloApp);
}
